/**
 * Get and structure meta data for routes
 */

// @REF: https://remix.run/docs/en/main/route/meta
// @NOTE: This may need revisiting if we have nested pages

import { LISTING_CANONICAL_PARAMS, LISTING_FILTER_PARAMS } from '~/utils/products'

// Type declarations
import type { MetaMatches } from '@remix-run/react/dist/routeModules'
import type { SanityCustomImage, RootDataProps } from '~/types'

/**
 * Gets a share image field and returns the relevant meta properties
 *
 * @param image
 * @returns
 */
interface ShareMetaProps {
  image: SanityCustomImage
}

export const getMetaShareImage = ({ image }: ShareMetaProps) => {
  const meta = []

  const imageUrl = image?.asset?.url ? image.asset.url : null

  if (imageUrl) {
    meta.push({
      property: 'og:image',
      content: imageUrl,
    })
    meta.push({
      property: 'twitter:image',
      content: imageUrl,
    })
    meta.push({
      property: 'twitter:card',
      content: 'summary_large_image',
    })
  }

  return meta
}

/**
 * Returns altered parent meta data for site pages
 *
 * @param {*} url
 * @returns
 */
interface MetaProps {
  matches: MetaMatches
  data: any
  /** Listing routes only. Every other route keeps the path only canonical */
  location?: { search: string }
}

export const getMeta = ({ matches, data, location }: MetaProps) => {
  // Root CMS data
  const rootMatch = matches.find(e => e.id == 'root')
  const rootData = rootMatch?.data as RootDataProps
  const settings = rootData?.site?.settings
  const fixedRoutes = rootData?.site?.global?.fixedRouteSeo
  const rootNoIndex = settings?.noIndex ?? false
  const rootUrl = rootData?.env?.SITE_URL ?? 'http://localhost:5173'

  // Active route
  const currentRouteMatch = matches[matches.length - 1]
  const currentRouteId = currentRouteMatch?.id
  const currentPathname = currentRouteMatch?.pathname

  // Fixed routes data
  const fixedRouteSeo = fixedRoutes?.find(
    e => 'routes/' + e.routeId == currentRouteId
  )

  // Loader page data
  const pageData = data?.seo

  // See if we have a page meta title set or fixed route description and if not fallback to title
  const metaTitle = pageData?.metaTitle
    ? pageData?.metaTitle
    : fixedRouteSeo?.seo?.metaTitle
      ? fixedRouteSeo.seo.metaTitle
      : data?.title
        ? data.title
        : ''

  // See if we have a page description set or fixed route description and if not return empty string
  const metaDescription = pageData?.metaDescription
    ? pageData.metaDescription
    : fixedRouteSeo?.seo?.metaDescription
      ? fixedRouteSeo.seo.metaDescription
      : ''

  // The five listing routes return this from their loader. Every other route
  // has none, so all the listing behaviour below stays switched off for them
  const listing = data?.listing as
    | { page: number; totalPages: number }
    | undefined
    | null

  // Listing pages carry their state in the query string, so their canonical has
  // to include it. Everything else keeps the path only URL it has always had
  const listingSearch = new URLSearchParams(location?.search ?? '')

  const buildListingUrl = (page: number) => {
    const params = new URLSearchParams()

    LISTING_CANONICAL_PARAMS.forEach(key => {
      if (key === 'page') {
        if (page > 1) {
          params.set('page', String(page))
        }

        return
      }

      listingSearch
        .getAll(key)
        .sort()
        .forEach(value => params.append(key, value))
    })

    const query = params.toString()
    const path = currentPathname == '/' ? '' : currentPathname

    return `${rootUrl}${path}${query ? `?${query}` : ''}`
  }

  // Filtered, sorted and search result URLs are thin permutations. Follow them
  // so the products stay discoverable, but keep them out of the index. The
  // canonical stays self referencing, never pointed at page one
  const isRefinedListing =
    listing != null &&
    (LISTING_FILTER_PARAMS.some(key => listingSearch.has(key)) ||
      listingSearch.has('query'))

  // See if there is a root no index, page index, fixed route index or allow all indexing
  const metaIndex = rootNoIndex
    ? 'noindex'
    : isRefinedListing
      ? 'noindex, follow'
      : pageData?.metaIndex
        ? pageData.metaIndex
        : fixedRouteSeo?.seo?.metaIndex
          ? fixedRouteSeo.seo.metaIndex
          : 'index, follow'

  // Combine elements to make the URL
  const metaUrl = listing
    ? buildListingUrl(listing.page)
    : currentPathname == '/'
      ? rootUrl
      : rootUrl + currentPathname

  // Meta data for page
  let meta = [
    { title: metaTitle },
    { name: 'robots', content: metaIndex },
    { tagName: 'link', rel: 'canonical', href: metaUrl },
    { property: 'og:url', content: metaUrl },
  ]

  // Optional description
  if (metaDescription) {
    meta.push({ name: 'description', content: metaDescription })
  }

  // Optional share image
  if (
    settings?.shareImage ||
    pageData?.shareImage ||
    fixedRouteSeo?.seo?.shareImage
  ) {
    const shareMeta = getMetaShareImage({
      image: pageData?.shareImage
        ? pageData.shareImage
        : fixedRouteSeo?.seo?.shareImage
          ? fixedRouteSeo.seo.shareImage
          : settings.shareImage,
    })
    meta = meta.concat(shareMeta)
  }

  // Secondary for Google since 2019, still read by other engines
  if (listing) {
    if (listing.page > 1) {
      meta.push({
        tagName: 'link',
        rel: 'prev',
        href: buildListingUrl(listing.page - 1),
      })
    }

    if (listing.page < listing.totalPages) {
      meta.push({
        tagName: 'link',
        rel: 'next',
        href: buildListingUrl(listing.page + 1),
      })
    }
  }

  return meta
}

/*
  Falls back to the product description when Brenda supplies no metaDescription,
  stripped of markup and cut to a length search engines will show.
*/
export const buildMetaDescription = (
  metaDescription?: string | null,
  fallback?: string | null,
  limit = 160
) => {
  if (metaDescription) return metaDescription

  const text = (fallback ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (text.length <= limit) return text

  // Cut on a word boundary rather than mid word
  const cut = text.slice(0, limit)
  const lastSpace = cut.lastIndexOf(' ')

  return `${lastSpace > 0 ? cut.slice(0, lastSpace) : cut}...`
}
